import React from 'react'
import { Link } from 'react-router-dom'
import { BaseView, baseStateToProps } from '../smart_bps/lib/base_view'
import { wrapView } from '../smart_bps/lib/utils'
import { AuthorizedDropDownMeny, UnauthorizedDropDownMenu } from '../lib/NavDropdownMenu'
import { SigurChoiceSelect } from '../lib/SigurChoiceSelect'
import { SigurPopover } from '../lib/SigurPopover'
import {
  GRID,
  buildLayout,
  displayLabelText,
  measureLabelBox,
} from './diagram_edges'

const KIND_BADGE = {
  button: { label: 'Кнопка', className: 'wfd-kind-badge--button' },
  api: { label: 'API', className: 'wfd-kind-badge--api' },
  cascade: { label: 'Каскад', className: 'wfd-kind-badge--cascade' },
}

/**
 * Палитра SVG: оранжевый — основной ДО и sequence;
 * синий / фиолетовый / зелёный — каскады, смежные ДО, события.
 */
const DIAGRAM = {
  accent: '#ff5a00',
  seqStroke: '#64748b',
  seqActive: '#ff5a00',
  msgStroke: '#3b82f6',
  msgActive: '#1d4ed8',
  msgArrow: '#2563eb',
  start: '#16a34a',
  end: '#dc2626',
  gateway: '#475569',
  textMuted: '#64748b',
  border: '#e4e6ea',
  nodeFill: '#ffffff',
  nodeStroke: '#566a7f',
  nodeAccent: '#ff5a00',
  nodeText: '#434a54',
  participantFill: '#faf5ff',
  participantStroke: '#7c3aed',
  participantAccent: '#7c3aed',
  participantText: '#5b21b6',
  labelFill: '#475569',
  msgLabelFill: '#1e40af',
}

const POOL_COLORS = {
  primary: { bg: 'rgba(255, 90, 0, 0.04)', border: 'rgba(255, 90, 0, 0.28)', header: '#ff5a00' },
  participant: { bg: '#faf5ff', border: '#c4b5fd', header: '#7c3aed' },
}

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v))
}

function isEventNode(pos) {
  const t = pos.node?.type
  return t === 'start' || t === 'end'
}

function nodeSize(pos) {
  if (isEventNode(pos)) {
    const d = GRID.eventR * 2
    return { w: d, h: d }
  }
  return { w: pos.w || GRID.nodeW, h: GRID.nodeH }
}

function eventCx(pos) {
  return pos.x + GRID.eventR
}

function eventCy(pos) {
  return pos.y + GRID.nodeH / 2
}

function EdgeLabel({ x, y, text, anchor = 'middle', fill = DIAGRAM.labelFill, fontWeight = '600' }) {
  const display = displayLabelText(text)
  if (!display) return null
  const w = measureLabelBox(display)
  const rectX = anchor === 'start' ? x - 4 : anchor === 'end' ? x - w + 4 : x - w / 2
  return (
    <g>
      <rect x={rectX - 1} y={y - 9} width={w + 2} height={18} fill="#ffffff" stroke={DIAGRAM.border} strokeWidth="0.75" />
      <text
        x={anchor === 'start' ? x : anchor === 'end' ? x : x}
        y={y + 4}
        textAnchor={anchor}
        fontSize="10"
        fill={fill}
        fontWeight={fontWeight}
      >
        {display}
      </text>
    </g>
  )
}

function KindBadge({ kind }) {
  const cfg = KIND_BADGE[kind] || KIND_BADGE.button
  return <span className={`wfd-kind-badge ${cfg.className}`}>{cfg.label}</span>
}

function ActorsCell({ actors, actorsLabel, actorDetails }) {
  const [openKey, setOpenKey] = React.useState(null)

  const whenByRole = React.useMemo(() => {
    const map = {}
    for (const item of actorDetails || []) {
      if (item?.role && item?.when) map[item.role] = item.when
    }
    return map
  }, [actorDetails])

  let labels = Array.isArray(actors) ? actors.filter(Boolean) : []
  if (!labels.length && actorsLabel) {
    labels = actorsLabel.split(/,\s*/).filter(Boolean)
  }
  if (!labels.length) return <span className="text-muted">—</span>

  return (
    <div className="wfd-actor-badges">
      {labels.map((name, idx) => {
        const when = whenByRole[name]
        const key = `${name}-${idx}`
        const badgeClass = `wfd-actor-badge${when ? ' wfd-actor-badge--hint' : ''}`

        if (!when) {
          return (
            <span key={key} className={badgeClass}>
              {name}
            </span>
          )
        }

        return (
          <SigurPopover
            key={key}
            className="wfd-actor-popover"
            placement="left"
            portal
            visible={openKey === key}
            trigger={(
              <span
                className={badgeClass}
                onMouseEnter={() => setOpenKey(key)}
                onMouseLeave={() => setOpenKey(null)}
              >
                {name}
              </span>
            )}
          >
            <span className="wfd-actor-popover__text">{when}</span>
          </SigurPopover>
        )
      })}
    </div>
  )
}

const NODE_HIGHLIGHT_PREFIX = 'node:'

function nodeHighlightId(nodeId) {
  return `${NODE_HIGHLIGHT_PREFIX}${nodeId}`
}

function parseNodeHighlightId(highlightId) {
  if (!highlightId?.startsWith(NODE_HIGHLIGHT_PREFIX)) return null
  return highlightId.slice(NODE_HIGHLIGHT_PREFIX.length)
}

function nodeStateCode(nodeId) {
  if (!nodeId) return null
  if (nodeId.startsWith('state_')) return nodeId.slice('state_'.length)
  return nodeId
}

function findGuideItem(guideItems, highlightId) {
  if (!highlightId || !guideItems?.length) return null
  return guideItems.find((g) => g.id === highlightId)
    || guideItems.find((g) => highlightId.includes(g.id) || g.id.includes(highlightId))
    || null
}

function findSpecEdge(spec, highlightId) {
  if (!highlightId || !spec?.edges?.length) return null
  return spec.edges.find((e) => e.id === highlightId)
    || spec.edges.find((e) => highlightId.includes(e.id) || e.id?.includes(highlightId))
    || null
}

/** Все рёбра на путях к узлу — рекурсивно до start / корня каскада. */
function collectIncomingEdgeIds(spec, nodeId, visited = new Set()) {
  if (!spec?.edges?.length || !nodeId || visited.has(nodeId)) return new Set()
  visited.add(nodeId)

  const ids = new Set()
  for (const e of spec.edges) {
    if (e.target !== nodeId) continue
    ids.add(e.id)
    if (e.source && e.source !== 'start') {
      for (const upstreamId of collectIncomingEdgeIds(spec, e.source, visited)) {
        ids.add(upstreamId)
      }
    }
  }
  return ids
}

function buildHighlightEdgeIds(spec, guideItems, highlightId) {
  if (!highlightId) return null

  const nodeId = parseNodeHighlightId(highlightId)
  if (nodeId) {
    const ids = collectIncomingEdgeIds(spec, nodeId)
    return ids.size ? ids : null
  }

  const guideItem = findGuideItem(guideItems, highlightId)
  if (guideItem?.id) return new Set([guideItem.id])

  const edge = findSpecEdge(spec, highlightId)
  if (edge?.id) return new Set([edge.id])

  return null
}

function isGuideRowHighlighted(row, highlightId, highlightEdgeIds) {
  if (!highlightId || !row) return false
  if (highlightEdgeIds?.has(row.id)) return true
  const nodeId = parseNodeHighlightId(highlightId)
  if (nodeId) return false
  return highlightId === row.id || highlightId.includes(row.id) || row.id.includes(highlightId)
}

/** Узлы «откуда» и «куда» для подсветки при hover на строке таблицы, стрелке или этапе. */
function buildHighlightNodeIds(spec, guideItems, highlightId, highlightEdgeIds) {
  if (!highlightId || !spec?.nodes?.length) return null

  const nodeIdSet = new Set(spec.nodes.map((n) => n.id))
  const nodeIds = new Set()

  const addRef = (ref) => {
    if (!ref) return
    if (nodeIdSet.has(ref)) {
      nodeIds.add(ref)
      return
    }
    const stateId = `state_${ref}`
    if (nodeIdSet.has(stateId)) nodeIds.add(stateId)
  }

  const hoveredNodeId = parseNodeHighlightId(highlightId)
  if (hoveredNodeId) {
    nodeIds.add(hoveredNodeId)
    if (highlightEdgeIds) {
      for (const edgeId of highlightEdgeIds) {
        const edge = spec.edges.find((e) => e.id === edgeId)
        if (edge?.source) nodeIds.add(edge.source)
        if (edge?.target) nodeIds.add(edge.target)
      }
    }
    return nodeIds.size ? nodeIds : null
  }

  const guideItem = findGuideItem(guideItems, highlightId)
  if (guideItem) {
    addRef(guideItem.from)
    addRef(guideItem.to)
  }

  const edge = findSpecEdge(spec, highlightId)
  if (edge) {
    nodeIds.add(edge.source)
    nodeIds.add(edge.target)
    if (edge.source?.startsWith('gw_')) {
      const incoming = spec.edges.find((e) => e.target === edge.source)
      if (incoming?.source) nodeIds.add(incoming.source)
    }
    if (edge.target?.startsWith('gw_')) {
      const outgoing = spec.edges.filter((e) => e.source === edge.target)
      outgoing.forEach((e) => {
        if (e.target) nodeIds.add(e.target)
      })
    }
  }

  return nodeIds.size ? nodeIds : null
}

function TransitionGuide({ items, highlightId, highlightEdgeIds, onHover }) {
  const rowRefs = React.useRef({})

  React.useEffect(() => {
    if (!highlightId) return
    const nodeId = parseNodeHighlightId(highlightId)
    if (nodeId && highlightEdgeIds?.size) {
      const first = items.find((g) => highlightEdgeIds.has(g.id))
      const row = first ? rowRefs.current[first.id] : null
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      return
    }
    const row = rowRefs.current[highlightId]
    row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [highlightId, highlightEdgeIds, items])

  if (!items?.length) return null

  const grouped = items.reduce((acc, item) => {
    const key = item.fromLabel || item.from || '—'
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  return (
    <div className="card card-action h-100 wfd-guide">
      <div className="card-header py-2 px-3 d-flex align-items-center gap-2">
        <span className="card-action-title mb-0">
          <i className="ti ti-list-details me-1" aria-hidden />
          Сценарии переходов
        </span>
        <span className="wfd-panel__count">{items.length}</span>
      </div>
      <div className="card-body p-0 d-flex flex-column" style={{ maxHeight: 'min(72vh, 640px)' }}>
        <div className="flex-grow-1 wfd-guide__scroll">
        {Object.entries(grouped).map(([fromLabel, rows]) => (
          <div key={fromLabel} className="wfd-guide__section">
            <div className="wfd-guide__section-title">{fromLabel}</div>
            <div className="wfd-guide__list">
              {rows.map((row) => (
                <div
                  key={row.id}
                  ref={(node) => {
                    if (node) rowRefs.current[row.id] = node
                    else delete rowRefs.current[row.id]
                  }}
                  className={`wfd-guide__item${isGuideRowHighlighted(row, highlightId, highlightEdgeIds) ? ' wfd-guide__item--active' : ''}`}
                  onMouseEnter={() => onHover?.(row.id)}
                  onMouseLeave={() => onHover?.(null)}
                >
                  <div className="wfd-guide__item-head">
                    <span className="wfd-guide__to">{row.toLabel || row.to}</span>
                    <KindBadge kind={row.kind} />
                  </div>
                  <div className="wfd-guide__item-action">
                    <span className="wfd-guide__action-label">{row.label}</span>
                    {row.event ? (
                      <code className="wfd-guide__event">{row.event}</code>
                    ) : null}
                  </div>
                  <ActorsCell
                    actors={row.actors}
                    actorsLabel={row.actorsLabel}
                    actorDetails={row.actorDetails}
                  />
                  {row.summary ? (
                    <div className="wfd-guide__summary">{row.summary}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  )
}

function ProcessDiagram({ spec, guideItems, highlightId, highlightEdgeIds, onHover }) {
  const layout = React.useMemo(() => buildLayout(spec), [spec])
  const highlightNodeIds = React.useMemo(
    () => buildHighlightNodeIds(spec, guideItems, highlightId, highlightEdgeIds),
    [spec, guideItems, highlightId, highlightEdgeIds],
  )
  const containerRef = React.useRef(null)
  const [view, setView] = React.useState({ scale: 1, tx: 0, ty: 0 })
  const dragRef = React.useRef(null)

  const fitView = React.useCallback(() => {
    if (!layout || !containerRef.current) return
    const cw = containerRef.current.clientWidth
    const ch = containerRef.current.clientHeight
    const scale = clamp(Math.min(cw / layout.width, ch / layout.height) * 0.92, 0.4, 1.2)
    setView({
      scale,
      tx: Math.max(16, (cw - layout.width * scale) / 2),
      ty: Math.max(8, (ch - layout.height * scale) / 2),
    })
  }, [layout])

  React.useEffect(() => {
    fitView()
    requestAnimationFrame(fitView)
  }, [fitView])

  if (!layout) return null

  const onWheel = (e) => {
    e.preventDefault()
    const factor = e.deltaY > 0 ? 0.92 : 1.08
    setView((v) => ({ ...v, scale: clamp(v.scale * factor, 0.35, 2.2) }))
  }

  const onPointerDown = (e) => {
    if (e.button !== 0) return
    dragRef.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.x
    const dy = e.clientY - drag.y
    setView((v) => ({ ...v, tx: drag.tx + dx, ty: drag.ty + dy }))
  }

  const onPointerUp = () => {
    dragRef.current = null
  }

  const zoomIn = () => setView((v) => ({ ...v, scale: clamp(v.scale * 1.15, 0.35, 2.2) }))
  const zoomOut = () => setView((v) => ({ ...v, scale: clamp(v.scale / 1.15, 0.35, 2.2) }))
  const resetView = () => fitView()

  const edgeActive = (e) => {
    if (!highlightId) return false
    if (highlightEdgeIds) return highlightEdgeIds.has(e.id)
    return e.id === highlightId || e.id?.includes(highlightId)
  }
  const nodeActive = (nodeId) => !highlightNodeIds || highlightNodeIds.has(nodeId)
  const nodeOpacity = (nodeId) => (highlightNodeIds && !nodeActive(nodeId) ? 0.35 : 1)

  const { nodeH, eventR, gwHalf } = GRID

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <div className="wfd-zoom btn-group btn-group-sm">
          <button type="button" className="btn btn-outline-secondary" onClick={zoomOut} title="Уменьшить">−</button>
          <button type="button" className="btn btn-outline-secondary" onClick={resetView} title="Вписать в экран">
            {Math.round(view.scale * 100)}%
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={zoomIn} title="Увеличить">+</button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="wfd-canvas wfd-canvas--grab"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <svg width="100%" height="100%" style={{ display: 'block' }}>
          <g transform={`translate(${view.tx},${view.ty}) scale(${view.scale})`}>
            <defs>
              <marker id="seqArrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <path d="M0,0 L8,3 L0,6 Z" fill={DIAGRAM.seqStroke} />
              </marker>
              <marker id="msgArrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <path d="M0,0 L8,3 L0,6 Z" fill={DIAGRAM.msgArrow} />
              </marker>
              <filter id="nodeShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.1" />
              </filter>
            </defs>

            {layout.pools.map((pool) => {
              const pl = layout.poolLayouts[pool.id]
              const colors = POOL_COLORS[pool.role === 'participant' ? 'participant' : 'primary']
              return (
                <g key={pool.id}>
                  <rect
                    x={pl.x}
                    y={pl.y}
                    width={pl.width}
                    height={pl.height}
                    fill={colors.bg}
                    stroke={colors.border}
                    strokeWidth="1.5"
                  />
                  <rect
                    x={pl.x}
                    y={pl.y}
                    width={GRID.poolHeader}
                    height={pl.height}
                    fill={colors.header}
                    opacity="0.12"
                  />
                  <line
                    x1={pl.x + GRID.poolHeader}
                    y1={pl.y}
                    x2={pl.x + GRID.poolHeader}
                    y2={pl.y + pl.height}
                    stroke={colors.border}
                    strokeWidth="1"
                  />
                  <text
                    x={pl.x + GRID.poolHeader / 2}
                    y={pl.y + pl.height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={colors.header}
                    fontSize="12"
                    fontWeight="700"
                    transform={`rotate(-90, ${pl.x + GRID.poolHeader / 2}, ${pl.y + pl.height / 2})`}
                  >
                    {pool.name}
                  </text>
                </g>
              )
            })}

            {layout.messageEdges.map((e) => {
              const active = edgeActive(e)
              const stroke = active ? DIAGRAM.msgActive : DIAGRAM.msgStroke
              const strokeW = active ? 2.5 : 1.75
              return (
                <g
                  key={`${e.id}-line`}
                  opacity={highlightId && !active ? 0.35 : 1}
                  onMouseEnter={() => onHover?.(e.id)}
                  onMouseLeave={() => onHover?.(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <polyline
                    points={e.points}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeW}
                    strokeDasharray="7 5"
                    strokeLinejoin="miter"
                    strokeLinecap="butt"
                    markerEnd="url(#msgArrow)"
                  />
                </g>
              )
            })}

            {layout.sequenceEdges.map((e) => {
              const active = edgeActive(e)
              const stroke = active ? DIAGRAM.seqActive : DIAGRAM.seqStroke
              const strokeW = active ? 2.25 : 1.75
              return (
                <g
                  key={`${e.id}-line`}
                  opacity={highlightId && !active ? 0.35 : 1}
                  onMouseEnter={() => onHover?.(e.id)}
                  onMouseLeave={() => onHover?.(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <polyline
                    points={e.points}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeW}
                    strokeDasharray={e.dashed ? '6 4' : undefined}
                    strokeLinejoin="miter"
                    strokeLinecap="butt"
                    markerEnd="url(#seqArrow)"
                  />
                  {e.question ? (
                    <text
                      x={e.questionX}
                      y={e.questionY}
                      textAnchor="middle"
                      fontSize="10"
                      fill={DIAGRAM.textMuted}
                      fontStyle="italic"
                    >
                      {e.question}
                    </text>
                  ) : null}
                </g>
              )
            })}

            {spec.nodes.map((n) => {
              const pos = layout.nodePositions[n.id]
              if (!pos) return null
              const { w: nodeW } = nodeSize(pos)
              const cx = pos.x + nodeW / 2
              const cy = pos.y + nodeH / 2

              if (n.type === 'start') {
                return (
                  <g key={n.id} filter="url(#nodeShadow)" opacity={nodeOpacity(n.id)}>
                    <circle
                      cx={eventCx(pos)}
                      cy={eventCy(pos)}
                      r={eventR}
                      fill="#fff"
                      stroke={DIAGRAM.start}
                      strokeWidth="2"
                    />
                  </g>
                )
              }

              if (n.type === 'end') {
                return (
                  <g key={n.id} filter="url(#nodeShadow)" opacity={nodeOpacity(n.id)}>
                    <circle
                      cx={eventCx(pos)}
                      cy={eventCy(pos)}
                      r={eventR}
                      fill="#fff"
                      stroke={DIAGRAM.end}
                      strokeWidth="3"
                    />
                  </g>
                )
              }

              if (n.type === 'gateway') {
                const pts = [
                  [cx, cy - gwHalf],
                  [cx + gwHalf, cy],
                  [cx, cy + gwHalf],
                  [cx - gwHalf, cy],
                ].map((p) => p.join(',')).join(' ')
                return (
                  <g key={n.id} filter="url(#nodeShadow)" opacity={nodeOpacity(n.id)}>
                    <polygon
                      points={pts}
                      fill="#fff"
                      stroke={DIAGRAM.gateway}
                      strokeWidth="1.5"
                    />
                    <text
                      x={cx}
                      y={cy + 4}
                      textAnchor="middle"
                      fontSize="13"
                      fontWeight="700"
                      fill={DIAGRAM.gateway}
                    >
                      ×
                    </text>
                  </g>
                )
              }

              const isParticipant = n.participant
              return (
                <g
                  key={n.id}
                  filter="url(#nodeShadow)"
                  opacity={nodeOpacity(n.id)}
                  onMouseEnter={() => onHover?.(nodeHighlightId(n.id))}
                  onMouseLeave={() => onHover?.(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    x={pos.x}
                    y={pos.y}
                    width={nodeW}
                    height={nodeH}
                    fill={isParticipant ? DIAGRAM.participantFill : DIAGRAM.nodeFill}
                    stroke={isParticipant ? DIAGRAM.participantStroke : DIAGRAM.nodeStroke}
                    strokeWidth={isParticipant ? 2 : 1.5}
                  />
                  <line
                    x1={pos.x}
                    y1={pos.y}
                    x2={pos.x + nodeW}
                    y2={pos.y}
                    stroke={isParticipant ? DIAGRAM.participantAccent : DIAGRAM.nodeAccent}
                    strokeWidth="2"
                  />
                  <text
                    x={pos.x + nodeW / 2}
                    y={pos.y + nodeH / 2 + 4}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="600"
                    fill={isParticipant ? DIAGRAM.participantText : DIAGRAM.nodeText}
                  >
                    {n.label}
                  </text>
                </g>
              )
            })}

            {layout.messageEdges.map((e) => {
              const active = edgeActive(e)
              if (!e.label?.trim()) return null
              return (
                <g
                  key={`${e.id}-label`}
                  opacity={highlightId && !active ? 0.35 : 1}
                  onMouseEnter={() => onHover?.(e.id)}
                  onMouseLeave={() => onHover?.(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <EdgeLabel
                    x={e.labelX}
                    y={e.labelY}
                    text={e.label}
                    anchor={e.labelAnchor || 'middle'}
                    fill={DIAGRAM.msgLabelFill}
                    fontWeight="700"
                  />
                </g>
              )
            })}

            {layout.sequenceEdges.map((e) => {
              const active = edgeActive(e)
              if (!e.label?.trim()) return null
              return (
                <g
                  key={`${e.id}-label`}
                  opacity={highlightId && !active ? 0.35 : 1}
                  onMouseEnter={() => onHover?.(e.id)}
                  onMouseLeave={() => onHover?.(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <EdgeLabel
                    x={e.labelX}
                    y={e.labelY}
                    text={e.label}
                    anchor={e.labelAnchor || 'middle'}
                  />
                </g>
              )
            })}
          </g>
        </svg>
      </div>
    </div>
  )
}

class DiagramPage extends BaseView {
  constructor(props) {
    super(props)
    this.state = {
      ...this.state,
      docTypes: [],
      typesError: '',
    }
    this.handleDocTypeChange = this.handleDocTypeChange.bind(this)
    this.loadDocTypes = this.loadDocTypes.bind(this)
  }

  componentDidMount() {
    this.loadDocTypes()
  }

  loadDocTypes() {
    fetch('/api/docs/types')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (!Array.isArray(data) || !data.length) {
          this.setState({
            docTypes: [],
            typesError: 'Нет документооборотов с переходами',
          })
          return
        }
        this.setState({ docTypes: data, typesError: '' })
        const fromRoute = this.props.router_params?.doc_type
        if (!fromRoute || !data.some((item) => item.value === fromRoute)) {
          this.redirect_to(`${this.docsBase}/${data[0].value}`)
        }
      })
      .catch((err) => {
        this.setState({
          docTypes: [],
          typesError: String(err?.message || err || 'Не удалось загрузить список ДО'),
        })
      })
  }

  get selectedDocType() {
    const fromRoute = this.props.router_params?.doc_type
    if (fromRoute) return fromRoute
    return this.state.docTypes[0]?.value || ''
  }

  get docsBase() {
    return '/docs/diagram'
  }

  handleDocTypeChange(value) {
    if (!value || value === this.selectedDocType) return
    this.redirect_to(`${this.docsBase}/${value}`)
  }

  renderTopPanelLeftItems() {
    const { docTypes, typesError } = this.state
    if (typesError) {
      return [
        <span key="wfd-doc-types-error" className="text-danger small">
          {typesError}
        </span>,
      ]
    }
    if (!docTypes.length) {
      return [
        <span key="wfd-doc-types-loading" className="text-muted small">
          Загрузка…
        </span>,
      ]
    }
    const items = docTypes.map((item) => [item.value, item.label])
    return [
      <span key="wfd-doc-select" className="wfd-toolbar__select-wrap">
        <SigurChoiceSelect
          name="doc_type"
          value={this.selectedDocType}
          items={items}
          onChange={this.handleDocTypeChange}
          className="form-select form-select-sm js-select2-sigur wfd-toolbar__select"
        />
      </span>,
    ]
  }

  renderTopPanelRightItems() {
    if (this.authorized) {
      return (
        <AuthorizedDropDownMeny
          userTitle={this.user_title}
          onLogout={this.logout}
        />
      )
    }
    return <UnauthorizedDropDownMenu />
  }

  renderBreadcrumbs() {
    return (
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Домой</Link>
          </li>
          <li className="breadcrumb-item active">Диаграмма переходов</li>
        </ol>
      </nav>
    )
  }

  renderContent() {
    if (!this.selectedDocType) {
      return null
    }
    return <DiagramBody selectedDocType={this.selectedDocType} />
  }
}

function DiagramBody({ selectedDocType }) {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [spec, setSpec] = React.useState(null)
  const [highlightId, setHighlightId] = React.useState(null)

  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    setSpec(null)
    setHighlightId(null)

    fetch(`/api/docs/diagram/${selectedDocType}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (cancelled) return
        setSpec(data)
      })
      .catch((err) => {
        if (cancelled) return
        setError(String(err?.message || err || 'Ошибка загрузки диаграммы'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedDocType])

  const guideItems = spec?.transitionGuide || []

  const highlightEdgeIds = React.useMemo(
    () => buildHighlightEdgeIds(spec, guideItems, highlightId),
    [spec, guideItems, highlightId],
  )

  const handleHover = React.useCallback((id) => {
    if (!id) {
      setHighlightId(null)
      return
    }
    if (parseNodeHighlightId(id)) {
      setHighlightId(id)
      return
    }
    const guideItem = findGuideItem(guideItems, id)
    setHighlightId(guideItem?.id ?? id)
  }, [guideItems])

  return (
    <div className="workflow-diagram">
      {loading && (
        <div className="wfd-empty mb-3 d-flex align-items-center gap-2">
          <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden />
          Загрузка…
        </div>
      )}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      {!loading && !error && spec && (
        <div className="row g-3 align-items-stretch">
          <div className="col-12 col-lg-8">
            <div className="card card-action h-100">
              <div className="card-header py-2 px-3">
                <span className="card-action-title mb-0">
                  <i className="ti ti-sitemap me-1" aria-hidden />
                  Диаграмма процесса
                </span>
              </div>
              <div className="card-body">
                <ProcessDiagram
                  spec={spec}
                  guideItems={guideItems}
                  highlightId={highlightId}
                  highlightEdgeIds={highlightEdgeIds}
                  onHover={handleHover}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            {guideItems.length > 0 ? (
              <TransitionGuide
                items={guideItems}
                highlightId={highlightId}
                highlightEdgeIds={highlightEdgeIds}
                onHover={handleHover}
              />
            ) : (
              <div className="card card-action h-100">
                <div className="card-body wfd-empty">Нет сценариев переходов.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default wrapView(DiagramPage, baseStateToProps)
